/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import IconTextBlock from '@component/common/block/icon-text-block';
import TextBlock from '@component/common/block/text-block';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';

type TPageNavigatorSize = 'small' | 'medium' | 'large';

interface IProps {
  currentPage: number;
  totalPage: number;
  theme?: ETheme;
  size?: TPageNavigatorSize;
  width?: string;
  height?: string;
  border?: boolean;
  handleChange: (page: number) => void;
}

const createDesign = (
  componentSize: TPageNavigatorSize,
  theme: ETheme,
  width: string | undefined,
  height: string | undefined,
  border: boolean,
) => {
  const createSize = () => {
    switch (componentSize) {
      case 'small':
        return { height: size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT, padding: size.DESIGN_SYSTEM_BUTTON_SMALL_PADDING_X };
      case 'large':
        return { height: size.DESIGN_SYSTEM_BUTTON_LARGE_HEIGHT, padding: size.DESIGN_SYSTEM_BUTTON_LARGE_PADDING_X };
      case 'medium':
      default:
        return { height: size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT, padding: size.DESIGN_SYSTEM_BUTTON_MEDIUM_PADDING_X };
    }
  };

  const createTheme = () => {
    switch (theme) {
      case ETheme.BLACK:
        return { backgroundColor: color.BLACK_2, textColor: color.WHITE_1, disabledColor: color.GRAY_2 };
      case ETheme.GRAY:
        return { backgroundColor: color.GRAY_5, textColor: color.BLACK_2, disabledColor: color.GRAY_2 };
      default:
        return { backgroundColor: color.WHITE_1, textColor: color.BLACK_2, disabledColor: color.GRAY_2 };
    }
  };

  const createEvent = () => ({ cursor: 'pointer' as const });
  const sizeDesign = createSize();
  const themeDesign = createTheme();

  return {
    width,
    height: height ?? sizeDesign.height,
    padding: sizeDesign.padding,
    backgroundColor: themeDesign.backgroundColor,
    textColor: themeDesign.textColor,
    disabledColor: themeDesign.disabledColor,
    shadow: border ? color.SURFACE_SHADOW : 'none',
    borderRadius: border ? size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS : '0px',
    event: createEvent(),
  };
};

const Wrapper = styled.div<{ $design: ReturnType<typeof createDesign> }>`
  display: flex;
  width: ${(props) => props.$design.width || 'fit-content'};
  height: ${(props) => props.$design.height || 'auto'};
  padding: 0 ${(props) => props.$design.padding};
  align-items: center;
  justify-content: center;
  gap: ${size.MARGIN_1};
  background-color: ${(props) => props.$design.backgroundColor};
  border-radius: ${(props) => props.$design.borderRadius};
  box-shadow: ${(props) => props.$design.shadow};
  box-sizing: border-box;
`;

const PageNavigator: React.FC<IProps> = ({
  currentPage,
  totalPage,
  theme = ETheme.WHITE,
  size: componentSize = 'medium',
  width,
  height,
  border = true,
  handleChange,
}) => {
  const design = createDesign(componentSize, theme, width, height, border);
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPage;
  const createItemDesign = (enabled: boolean) => ({
    box: { gap: '3px', event: { cursor: enabled ? design.event.cursor : ('auto' as const) } },
    text: { size: componentSize, color: enabled ? design.textColor : design.disabledColor },
    icon: { size: componentSize, color: enabled ? design.textColor : design.disabledColor },
  });

  return (
    <Wrapper $design={design}>
      <IconTextBlock
        icon='back'
        text='이전'
        design={createItemDesign(hasPrevious)}
        click={{ handler: () => hasPrevious && handleChange(currentPage - 1) }}
      />
      <TextBlock
        text={`${currentPage} / ${totalPage}`}
        design={{ text: { size: componentSize, color: design.textColor, bold: true } }}
      />
      <IconTextBlock
        icon='forward'
        text='다음'
        arrangement='text-icon'
        design={createItemDesign(hasNext)}
        click={{ handler: () => hasNext && handleChange(currentPage + 1) }}
      />
    </Wrapper>
  );
};

export default PageNavigator;
